import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EXPORT_COLUMNS = [
  "S.No",
  "Zone",
  "Ward",
  "Action Category",
  "Remarks",
  "Valid From",
  "Valid Till",
  "Status",
  "Last Modified On",
];

const formatDisplayDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const mapRowsForExport = (data = []) =>
  data.map((row, index) => [
    index + 1,
    row.zoneName || "N/A",
    row.wardName || row.wardNo || "N/A",
    row.actionCategoryName || row.actionCategory || "N/A",
    row.remarks || "N/A",
    formatDisplayDate(row.validFrom),
    formatDisplayDate(row.validTill || row.validTo),
    row.status || (row.isActive ? "Active" : "Inactive"),
    formatDisplayDateTime(row.lastModifiedOn),
  ]);

export const ExportSlaExceptionRule = ({
  slaExceptionRuleData = [],
  loadExportData,
}) => {
  const resolveExportData = async () => {
    if (typeof loadExportData === "function") {
      const data = await loadExportData();
      return Array.isArray(data) ? data : slaExceptionRuleData;
    }

    return slaExceptionRuleData;
  };

  const exportToExcel = async () => {
    const rows = await resolveExportData();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SLA Exception Rules");
    const boldFont = { bold: true };

    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `SLA Exception Rule Generated on: ${formattedDate}, ${formattedTime}`;

    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, EXPORT_COLUMNS.length);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    worksheet.addRow(["", "", "SLA Exception Rule List", ""]);
    worksheet.mergeCells(2, 2, 2, EXPORT_COLUMNS.length);
    worksheet.getCell(2, 2).value = "SLA Exception Rule List";
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    const tableHeaderRow = worksheet.addRow(EXPORT_COLUMNS);
    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center" };
      if (colNumber >= 1 && colNumber <= EXPORT_COLUMNS.length) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "003366" },
        };
      }
    });

    mapRowsForExport(rows).forEach((row) => {
      worksheet.addRow(row).eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "SlaExceptionRule.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    }
  };

  const exportToPDF = async () => {
    const rows = await resolveExportData();
    const doc = new jsPDF({ orientation: "landscape" });
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `SLA Exception Rule Generated on: ${formattedDate} ${formattedTime}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("SLA Exception Rule List", 14, 20);
    doc.setFontSize(10);
    doc.text(downloadDateTime, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [EXPORT_COLUMNS],
      body: mapRowsForExport(rows),
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8,
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 30 },
        2: { cellWidth: 28 },
        3: { cellWidth: 38 },
        5: { cellWidth: 26 },
        6: { cellWidth: 26 },
        7: { cellWidth: 22 },
        8: { cellWidth: 35 },
      },
    });

    doc.save("SlaExceptionRule.pdf");
  };

  return (
    <>
      <button type="button" onClick={exportToExcel} className="button-container ms-3">
        <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
      </button>
      <button type="button" onClick={exportToPDF} className="button-container ms-3">
        <i className="fa-solid fa-file-pdf me-1 icon-container"></i> PDF
      </button>
    </>
  );
};
